// Who can take a call right now, who is on one (and with whom), and who has
// gone — with when each was last seen. Read from the gateway, which tracks
// every agent's status and console check-ins; refreshed on voice events and
// on a slow poll in case events are not arriving.

import { useCallback, useEffect, useState } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { voiceService, type RosterAgent } from '@/services/voice.service';
import { useWebSocket } from '@/hooks/useWebSocket';

const POLL_MS = 15_000;

type Tone = { label: string; wash: string; line: string; ink: string; dot: string };

/** One label and tone per thing an agent can be doing. */
export function rosterStatus(agent: RosterAgent): Tone {
  if (agent.status === 'busy') {
    return { label: 'On a call', wash: 'var(--co-burgundy-wash)', line: 'var(--co-burgundy-line)', ink: 'var(--co-burgundy)', dot: 'var(--co-burgundy)' };
  }
  if (agent.status === 'reserved') {
    return { label: 'Ringing', wash: 'var(--co-terracotta-wash)', line: 'var(--co-terracotta-line)', ink: 'var(--co-terracotta-ink)', dot: 'var(--co-terracotta)' };
  }
  if (agent.status === 'available' && agent.stale) {
    return { label: 'Not responding', wash: 'var(--co-sunken)', line: 'var(--co-line)', ink: 'var(--co-ink-3)', dot: 'var(--co-terracotta)' };
  }
  if (agent.status === 'available') {
    return { label: 'Available', wash: 'var(--co-green-wash)', line: 'var(--co-green-line)', ink: 'var(--co-green-ink)', dot: 'var(--co-green)' };
  }
  return { label: 'Offline', wash: 'var(--co-sunken)', line: 'var(--co-line)', ink: 'var(--co-ink-3)', dot: 'var(--co-line-strong)' };
}

/** "just now", "4 min ago", "2 h ago". */
export function lastSeenLabel(at: number | undefined, now = Date.now()): string {
  if (!at) return '—';
  if (now - at < 60_000) return 'just now';
  return `${formatDistanceToNowStrict(at, { roundingMethod: 'floor' })} ago`;
}

interface AgentRosterProps {
  /** The signed-in agent, labelled "(you)". */
  currentUserId?: string;
  /** Opens the call an agent is on. */
  onOpenCall?: (sessionId: string) => void;
  /** Reports the count of agents who can take a call now. */
  onAvailableCount?: (count: number) => void;
}

export function AgentRoster({ currentUserId, onOpenCall, onAvailableCount }: AgentRosterProps) {
  const [agents, setAgents] = useState<RosterAgent[] | null>(null);
  const [error, setError] = useState(false);
  const { on, off } = useWebSocket();

  const load = useCallback(async () => {
    try {
      const roster = await voiceService.getAgentRoster();
      setAgents(roster);
      setError(false);
      onAvailableCount?.(roster.filter((a) => a.status === 'available' && !a.stale).length);
    } catch {
      setError(true);
    }
  }, [onAvailableCount]);

  useEffect(() => {
    void load();
    const poll = setInterval(load, POLL_MS);
    const refresh = () => void load();
    const events = ['voice:handoff:updated', 'voice:call:with_agent', 'voice:call:ended'];
    events.forEach((e) => on(e, refresh));
    return () => {
      clearInterval(poll);
      events.forEach((e) => off(e, refresh));
    };
  }, [load, on, off]);

  if (agents === null) {
    return (
      <p className="px-[18px] py-4 text-[12.5px] text-[var(--co-ink-3)]">
        {error ? 'The agent list could not be loaded.' : 'Loading agents…'}
      </p>
    );
  }
  if (agents.length === 0) {
    return (
      <p className="px-[18px] py-4 text-[12.5px] text-[var(--co-ink-3)]">
        No agent has been online today. Agents appear here when they go online.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-[var(--co-line-subtle)]" aria-label="Agents">
      {agents.map((agent) => {
        const tone = rosterStatus(agent);
        const name = agent.agentName || 'Unnamed agent';
        const onCall = (agent.status === 'busy' || agent.status === 'reserved') && agent.sessionId;
        return (
          <li key={agent.agentId} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-[18px] py-[10px]">
            <span
              className="flex h-8 w-8 flex-none items-center justify-center rounded-full text-[12px] font-bold"
              style={{ background: 'var(--co-selected)', color: 'var(--co-ink-2)' }}
              aria-hidden
            >
              {name.charAt(0).toUpperCase()}
            </span>
            <div className="min-w-[140px] flex-1">
              <p className="truncate text-[13px] font-semibold text-[var(--co-ink)]">
                {name}
                {agent.agentId === currentUserId && <span className="font-normal text-[var(--co-ink-3)]"> (you)</span>}
              </p>
              <p className="text-[11.5px] text-[var(--co-ink-3)]">
                {agent.status === 'offline' ? 'Last seen ' : 'Checked in '}
                {lastSeenLabel(agent.lastSeenAt)}
                {agent.stale && ' — calls are not sent to them'}
              </p>
            </div>
            {onCall && (
              <button
                type="button"
                onClick={() => onOpenCall?.(agent.sessionId!)}
                className="co-mono text-[11.5px] text-[var(--co-brass-ink)] hover:underline"
                title="Open this call"
              >
                {agent.status === 'reserved' ? 'Ringing for ' : 'With '}
                {voiceService.formatPhoneNumber(agent.callerNumber || '') || 'a caller'}
              </button>
            )}
            <span
              className="co-mono flex flex-none items-center gap-[6px] rounded-full border px-[9px] py-[3px] text-[9.5px] font-semibold uppercase tracking-[.1em]"
              style={{ background: tone.wash, borderColor: tone.line, color: tone.ink }}
            >
              <span className="h-[6px] w-[6px] rounded-full" style={{ background: tone.dot }} />
              {tone.label}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export default AgentRoster;
